import React from "react";
import { API } from "@/lib/api";
import { ArrowSquareOut, CheckCircle, XCircle } from "@phosphor-icons/react";

export function CompetitorsSection({ items }) {
  if (!items || items.length === 0) {
    return <div className="text-sm text-zinc-500">No competitors identified.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
      {items.map((c, i) => (
        <div key={i} className="bg-white p-4" data-testid={`competitor-${i}`}>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-display font-semibold text-zinc-950 text-base">{c.name}</span>
            {c.verified ? (
              <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-emerald-700">
                <CheckCircle size={11} weight="fill" /> verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-red-700">
                <XCircle size={11} weight="fill" /> unverified
              </span>
            )}
          </div>
          <a
            href={c.homepage_url}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-flex items-center gap-1 font-mono text-xs text-zinc-600 hover:text-zinc-900 hover:underline break-all"
          >
            {c.homepage_url} <ArrowSquareOut size={11} />
          </a>
          {c.title && <div className="mt-2 text-xs text-zinc-700">{c.title}</div>}
          {c.similarity_reason && (
            <div className="mt-2 text-sm text-zinc-800 leading-relaxed">{c.similarity_reason}</div>
          )}
          {c.overlap_notes && (
            <div className="mt-2 border-t border-zinc-100 pt-2 text-xs text-zinc-600">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                Overlap
              </span>
              <div className="mt-1">{c.overlap_notes}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SentimentTag({ sentiment }) {
  const map = {
    positive: { bg: "#ECFDF5", text: "#059669", border: "#6EE7B7" },
    negative: { bg: "#FEF2F2", text: "#DC2626", border: "#FCA5A5" },
    mixed: { bg: "#FFFBEB", text: "#D97706", border: "#FCD34D" },
    neutral: { bg: "#F4F4F5", text: "#52525B", border: "#D4D4D8" },
  };
  const s = map[sentiment] || map.mixed;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest border"
      style={{ background: s.bg, color: s.text, borderColor: s.border }}
    >
      {sentiment}
    </span>
  );
}

export function ReviewsSection({ reviews }) {
  if (!reviews) {
    return <div className="text-sm text-zinc-500">No reviews mined yet.</div>;
  }
  return (
    <div>
      <div className="mb-4 grid grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        <div className="bg-white p-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Source</div>
          <div className="font-display text-sm text-zinc-950 mt-0.5">{reviews.source}</div>
        </div>
        <div className="bg-white p-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Posts examined</div>
          <div className="font-display text-sm text-zinc-950 mt-0.5">{reviews.posts_examined}</div>
        </div>
        <div className="bg-white p-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Queries</div>
          <div className="font-mono text-xs text-zinc-700 mt-0.5 truncate">
            {(reviews.query_terms || []).join(", ") || "—"}
          </div>
        </div>
      </div>

      {reviews.note && (
        <div className="mb-4 border border-dashed border-zinc-400 bg-zinc-50 p-3 text-sm text-zinc-700 font-mono">
          {reviews.note}
        </div>
      )}

      {(reviews.insights || []).length === 0 && (
        <div className="text-sm text-zinc-500">No themed insights extracted.</div>
      )}

      <div className="space-y-3">
        {(reviews.insights || []).map((ins, i) => (
          <div key={i} className="border border-zinc-200 bg-white" data-testid={`review-insight-${i}`}>
            <div className="px-4 py-2 border-b border-zinc-200 flex items-center gap-2 flex-wrap bg-zinc-50">
              <span className="font-mono text-sm text-zinc-900">{ins.theme}</span>
              <SentimentTag sentiment={ins.sentiment} />
              <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                freq · {ins.frequency}
              </span>
            </div>
            <div className="p-4">
              <p className="text-sm text-zinc-800 leading-relaxed">{ins.summary}</p>
              {ins.example_quotes?.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {ins.example_quotes.map((q, j) => (
                    <div key={j} className="text-sm text-zinc-700 italic border-l-2 border-zinc-950 pl-3">
                      &ldquo;{q}&rdquo;
                    </div>
                  ))}
                </div>
              )}
              {ins.evidence_urls?.length > 0 && (
                <div className="mt-3 pt-2 border-t border-zinc-100 space-y-0.5">
                  {ins.evidence_urls.map((u, j) => (
                    <a
                      key={j}
                      href={u}
                      target="_blank"
                      rel="noreferrer"
                      className="block font-mono text-[11px] text-zinc-500 hover:text-zinc-900 hover:underline break-all"
                    >
                      ↳ {u}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DiffSection({ diff }) {
  if (!diff) {
    return (
      <div className="text-sm text-zinc-500">
        No prior analysis of this URL — first run establishes the baseline.
      </div>
    );
  }
  const Row = ({ label, arr, color }) => (
    <div className="border border-zinc-200 bg-white p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
        {label} · {(arr || []).length}
      </div>
      {(arr || []).length === 0 ? (
        <div className="text-sm text-zinc-500">—</div>
      ) : (
        <ul className="space-y-1 text-sm" style={{ color }}>
          {arr.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="font-mono text-xs mt-0.5">•</span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
  return (
    <div>
      <div className="mb-4 border-l-2 border-zinc-950 pl-3 text-sm text-zinc-800">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
          vs prior run · {new Date(diff.previous_created_at).toLocaleString()}
        </div>
        <div className="font-medium">{diff.summary}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Row label="Features added" arr={diff.features_added} color="#059669" />
        <Row label="Features removed" arr={diff.features_removed} color="#DC2626" />
        <Row label="Tech added" arr={diff.tech_added} color="#059669" />
        <Row label="Tech removed" arr={diff.tech_removed} color="#DC2626" />
      </div>
      {diff.pricing_changes?.length > 0 && (
        <div className="mt-3 border border-zinc-200 bg-white p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
            Pricing changes
          </div>
          <ul className="space-y-1 text-sm text-zinc-800">
            {diff.pricing_changes.map((s, i) => (
              <li key={i} className="font-mono">↳ {s}</li>
            ))}
          </ul>
        </div>
      )}
      {diff.positioning_changed && (
        <div className="mt-3 border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 font-mono">
          Positioning statement has shifted since the prior run.
        </div>
      )}
    </div>
  );
}

export function RICECell({ item }) {
  if (item.rice_score == null) return null;
  return (
    <div className="mt-3 pt-3 border-t border-zinc-100">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
        RICE score
      </div>
      <div className="flex items-baseline gap-3 flex-wrap">
        <div className="font-display text-2xl font-bold text-zinc-950 leading-none">
          {item.rice_score}
        </div>
        <div className="font-mono text-[11px] text-zinc-600">
          R {item.reach} · I {item.impact_score} · C {item.confidence_pct}% · E {item.effort_weeks}w
        </div>
      </div>
    </div>
  );
}

export function ScreenshotStrip({ analysisId, pages }) {
  const withShots = (pages || []).filter((p) => p.screenshot_path);
  if (withShots.length === 0) return null;
  return (
    <div className="border border-zinc-200 bg-white" data-testid="screenshot-strip">
      <div className="px-5 py-3 border-b border-zinc-200 font-mono text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-50">
        Screenshots · {withShots.length}
      </div>
      <div className="p-3 grid grid-cols-2 gap-2">
        {withShots.map((p, i) => (
          <a
            key={i}
            href={`${API}/screenshots/${p.screenshot_path}`}
            target="_blank"
            rel="noreferrer"
            className="group block border border-zinc-200 hover:border-zinc-950 overflow-hidden"
            title={p.url}
          >
            <img
              src={`${API}/screenshots/${p.screenshot_path}`}
              alt={p.page_type}
              className="w-full h-auto block max-h-40 object-cover object-top group-hover:opacity-90"
              loading="lazy"
            />
            <div className="px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500 border-t border-zinc-100">
              {p.page_type}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
